import React, { useState } from 'react';
import { Box, Typography, Button, Chip, Switch, Alert, Tooltip, TextField, IconButton, Divider } from '@mui/material';
import {
  Calculate as MathIcon,
  Chat as TestDriveIcon,
  Explore as HintIcon,
  Timer as PacingIcon,
  SentimentSatisfiedAlt as ToneIcon,
  RateReview as FeedbackIcon,
  Psychology as MisconceptionIcon,
  WavingHand as WelcomeIcon,
  Extension as ActivitiesIcon,
  School as ClassesIcon,
  Badge as IdentityIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  FiberManualRecord as UnsavedIcon,
} from '@mui/icons-material';
import { tutorTokens } from '../../../theme/tutorTheme';
import {
  HINT_STYLES,
  PACING_OPTIONS,
  TONE_OPTIONS,
  FEEDBACK_STYLES,
  saveAgent,
} from '../../../utils/agentTutorStorage';
import { focusRingSx } from './SelectableTile';
import OptionCard from './OptionCard';
import { SectionTitle, IconBadge } from './PanelTitles';
import TutoringRulesField from './TutoringRulesField';
import normalizeAgent, { nextLocalId } from './normalizeAgent';

// The same p:3 / radiusLg / surfaceRaised card every *interactive* panel on
// this page uses — was declared inline three separate times (Hinting,
// Activities, Assigned Classes) before this, which is exactly how the
// layout would've drifted from a fourth card added later.
const cardSx = {
  p: tutorTokens.layout.cardPad,
  borderRadius: `${tutorTokens.radiusLg}px`,
  backgroundColor: tutorTokens.surfaceRaised,
  border: `1px solid ${tutorTokens.border}`,
  boxShadow: tutorTokens.shadowCard,
};

// These six panels used to split into a hand-picked "one card left, five
// stacked right" layout, then briefly a `columnCount: 2` multicol flow —
// both eventually mis-balance because Misconceptions has no upper bound (a
// teacher can add as many as they want). A CSS-multicol `breakInside:
// 'avoid'` card can't be split once it's taller than the balance point, so
// a long Misconceptions list still forced one column short and the other
// tall; worse, every add/remove re-runs the browser's balancing and can
// reassign *other* cards to the opposite column mid-edit — a layout jump
// while a teacher is typing.
//
// The actual fix is structural, not a smarter balancing algorithm: pull
// the one unbounded panel (Misconceptions) out of the two-column area
// entirely and give it its own full-width row below. The five panels that
// stay in the two-column grid — Hinting/Pacing/Tone/Feedback (one panel),
// Welcome Message, Tutoring Rules, Activities, Assigned Classes — are all
// genuinely bounded (fixed option lists, a capped textarea, three toggle
// rows, a small chip set), so a plain static split balances them well and
// can never re-flow while someone's mid-edit, because nothing in that
// region changes height unpredictably.

// Deliberately not `cardSx` — the Identity strip is read-only, unlike the
// six interactive cards around it, and used to be visually identical to
// them (same white ground, same shadow, same weight), which read as "this
// is editable too" until you tried it. A muted ground, dashed border (the
// same "not a normal control" language the wizard already uses for its
// disabled Marketplace tile) and no shadow mark it as settled rather than
// broken/disabled.
const identitySx = {
  p: 2.5,
  borderRadius: `${tutorTokens.radiusLg}px`,
  backgroundColor: tutorTokens.surface,
  border: `1px dashed ${tutorTokens.borderStrong}`,
  boxShadow: 'none',
};


const MathTutorConfig = ({ agent, groups = [], currentUser, onBack, onOpenBuilder, onTestDrive }) => {
  const [local, setLocal] = useState(() => normalizeAgent(agent));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [misconceptionDraft, setMisconceptionDraft] = useState({ mistake: '', response: '' });

  const update = (patch) => {
    setLocal((a) => ({ ...a, ...patch }));
    setDirty(true);
  };

  const toggleGroup = (id) =>
    update({ assignedGroupIds: local.assignedGroupIds.includes(id) ? local.assignedGroupIds.filter((x) => x !== id) : [...local.assignedGroupIds, id] });

  const addMisconception = () => {
    if (!misconceptionDraft.mistake.trim()) return;
    update({ misconceptions: [...local.misconceptions, { id: nextLocalId(), ...misconceptionDraft }] });
    setMisconceptionDraft({ mistake: '', response: '' });
  };

  const removeMisconception = (id) => update({ misconceptions: local.misconceptions.filter((m) => m.id !== id) });

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // Save the agent's *current* status — never force-publish. Editing a
      // draft's hint style here used to flip it to active and push it live to
      // students with no confirmation.
      const saved = normalizeAgent(await saveAgent(local, { publish: local.status === 'active', currentUser }));
      setLocal(saved);
      setDirty(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // The test drive loads the agent from the API, so pending edits would be
  // invisible there — save them first, otherwise a teacher tweaks the hint
  // style, hits Test Drive, and watches the old behaviour.
  const handleTestDrive = async () => {
    setSaving(true);
    setError('');
    try {
      let target = local;
      if (dirty) {
        target = normalizeAgent(await saveAgent(local, { publish: local.status === 'active', currentUser }));
        setLocal(target);
        setDirty(false);
      }
      onTestDrive && onTestDrive(target.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // "Edit in builder" and "+ Assign" both leave this screen entirely — if
  // there are unsaved edits sitting under the "Unsaved changes" indicator,
  // navigating away silently discarded them. Same save-first pattern as
  // Test Drive above, not a confirm dialog: saving cleanly is what this
  // file already does everywhere else a navigation could lose work.
  //
  // Passes the (possibly just-saved) agent out through `onOpenBuilder` —
  // the caller only has whatever `agent` it was mounted with, which is
  // stale the instant a save here succeeds. Without this, the save hits the
  // server correctly but the builder reopens on the pre-edit object, and an
  // untouched Publish/Save-as-draft from there PUTs that stale object right
  // back over the edit that just landed.
  const openBuilder = async () => {
    if (!dirty) {
      onOpenBuilder && onOpenBuilder(local);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const saved = normalizeAgent(await saveAgent(local, { publish: local.status === 'active', currentUser }));
      setLocal(saved);
      setDirty(false);
      onOpenBuilder && onOpenBuilder(saved);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // The breadcrumb back link leaves this screen the same way "Edit in
  // builder" / "+ Assign" do — it needs the same save-first guard, or
  // dirty edits vanish with no indication anything was lost.
  const handleBack = async () => {
    if (!dirty) {
      onBack && onBack();
      return;
    }
    setSaving(true);
    setError('');
    try {
      const saved = normalizeAgent(await saveAgent(local, { publish: local.status === 'active', currentUser }));
      setLocal(saved);
      setDirty(false);
      onBack && onBack();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const classCount = local.assignedGroupIds.length;

  // No `minHeight: '100vh'` on the root Box below — this page only ever
  // mounts inside AdminDashboardV2's own `component="main"`, which already
  // sets `minHeight: '100vh'` and the same background color. Stacking a
  // second 100vh on top of that main region's own 100vh (plus its Toolbar
  // spacer) was pure page-level bloat: a real, if modest, page taller than
  // the viewport for content that doesn't need it.
  return (
    // This page previously had no width cap at all (full-bleed inside the
    // admin shell's own content area) — on a wide display its six panels
    // ran genuinely edge to edge with no margin. Now shares the same
    // fluid `tutorTokens.layout.maxWidth` frame as every other Agent
    // Tutor page.
    <Box sx={{ p: tutorTokens.layout.gutter, backgroundColor: tutorTokens.surface }}>
      <Box sx={{ maxWidth: tutorTokens.layout.maxWidth, mx: 'auto' }}>
      <Box
        component="button"
        type="button"
        onClick={handleBack}
        disabled={saving}
        sx={{
          display: 'inline-block',
          background: 'none',
          border: 'none',
          p: 0,
          mb: 1,
          font: 'inherit',
          ...tutorTokens.type.body,
          opacity: saving ? 0.6 : 1,
          color: tutorTokens.inkSoft,
          cursor: saving ? 'not-allowed' : 'pointer',
          '&:hover': saving ? {} : { color: tutorTokens.accent },
          ...focusRingSx,
        }}
      >
        Agent Tutor › {local.name}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: `${tutorTokens.radiusMd}px`,
              backgroundColor: tutorTokens.accentLight,
              color: tutorTokens.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MathIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: tutorTokens.ink }}>
              {local.name}
            </Typography>
            <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft }}>
              Math · {local.grades.join(', ')} · Assigned to {classCount} class{classCount === 1 ? '' : 'es'} · Last edited by {local.lastEditedBy || 'you'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {dirty && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: tutorTokens.partial, ...tutorTokens.type.label }}>
              <UnsavedIcon sx={{ fontSize: 8 }} aria-hidden="true" />
              Unsaved changes
            </Box>
          )}
          <Tooltip title="Saves your changes first, then opens the real workbench">
            <span>
              <Button
                variant="outlined"
                startIcon={<TestDriveIcon />}
                disabled={saving}
                onClick={handleTestDrive}
                sx={{
                  borderColor: tutorTokens.borderStrong,
                  color: tutorTokens.ink,
                  fontWeight: 700,
                  '&:hover': { borderColor: tutorTokens.accent, backgroundColor: tutorTokens.accentLight },
                  ...focusRingSx,
                }}
              >
                {saving ? 'Starting…' : dirty ? 'Save & Test Drive' : 'Test Drive'}
              </Button>
            </span>
          </Tooltip>
          {dirty ? (
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              sx={{
                backgroundColor: tutorTokens.accent,
                fontWeight: 700,
                boxShadow: tutorTokens.shadowCard,
                transition: `background-color ${tutorTokens.durFast}s ease, box-shadow ${tutorTokens.durFast}s ease, transform ${tutorTokens.durFast}s ease`,
                '&:hover': { backgroundColor: tutorTokens.accentDark, boxShadow: tutorTokens.shadowHover },
                '&:active': { transform: 'scale(0.97)' },
                ...focusRingSx,
              }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          ) : (
            // Nothing to commit — say so plainly rather than leaving a greyed-out
            // "Save Changes" button whose disabled reason isn't obvious.
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.75,
                py: 0.85,
                borderRadius: `${tutorTokens.radiusSm}px`,
                border: `1px solid ${tutorTokens.correctBorder}`,
                backgroundColor: tutorTokens.correctBg,
                color: tutorTokens.correct,
                ...tutorTokens.type.label,
                fontWeight: 700,
              }}
            >
              <CheckIcon sx={{ fontSize: 16 }} aria-hidden="true" />
              Saved
            </Box>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2.5, borderRadius: `${tutorTokens.radiusSm}px` }}>
          {error}
        </Alert>
      )}

      <Box sx={{ ...identitySx, mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
        <SectionTitle icon={IdentityIcon} sx={{ mb: 0, flexShrink: 0 }}>Identity</SectionTitle>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 1 }}>
          {[
            ['Name', local.name],
            ['Subject', 'Math'],
            ['Grades', local.grades.join(', ') || '—'],
          ].map(([label, value]) => (
            <Box key={label}>
              <Typography sx={{ ...tutorTokens.type.eyebrow, textTransform: 'uppercase', color: tutorTokens.inkFaint }}>
                {label}
              </Typography>
              <Typography sx={{ ...tutorTokens.type.bodyLg, fontWeight: 600, color: tutorTokens.ink }}>{value}</Typography>
            </Box>
          ))}
        </Box>
        <Tooltip title="Name, subject and grades are scope, not tuning — change them in the builder so a mid-session student's reading level never shifts. Saves your other edits first.">
          <span>
            <Button
              disabled={saving}
              onClick={openBuilder}
              sx={{ color: tutorTokens.accent, fontWeight: 700, minHeight: 44, flexShrink: 0, ...focusRingSx }}
            >
              {saving ? 'Saving…' : dirty ? 'Save & Edit in Builder' : 'Edit in builder'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: tutorTokens.layout.sectionGap }}>
        <Box sx={cardSx}>
          {/* Hint and Pacing stack in one column always — this card is
              already half of a two-column page grid (~590px at the shared
              1240 frame), so a further internal 2-up split (the old
              `twoColAt760`, which switched on *viewport* width and had no
              way to see this card's own ~590px) squeezed each option down
              to ~280px, the exact width that broke 4-6 word labels across
              2-3 lines. See AgentBuilderWizard.js's step 2 for the same
              fix and the fuller rationale. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: tutorTokens.layout.sectionGap }}>
            <Box>
              <SectionTitle icon={HintIcon} sx={{ mb: 1.5 }}>When a student is stuck, your agent should…</SectionTitle>
              <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft, mb: 1.5 }}>How your agent helps a stuck student</Typography>
              <Box role="radiogroup" aria-label="Hinting strategy">
                {HINT_STYLES.map((h) => (
                  <OptionCard
                    key={h.value}
                    selected={local.hintStyle === h.value}
                    label={h.label}
                    consequence={h.example}
                    onSelect={() => update({ hintStyle: h.value })}
                    hoverLift={false}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <SectionTitle icon={PacingIcon} sx={{ mb: 1.5 }}>When should it move to the next topic?</SectionTitle>
              <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft, mb: 1.5 }}>When the agent moves to the next topic</Typography>
              <Box role="radiogroup" aria-label="Pacing and mastery">
                {PACING_OPTIONS.map((p) => (
                  <OptionCard
                    key={p.value}
                    selected={local.pacing === p.value}
                    label={p.label}
                    consequence={p.hint}
                    onSelect={() => update({ pacing: p.value })}
                    hoverLift={false}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: tutorTokens.border }} />

          <SectionTitle icon={ToneIcon} sx={{ mb: 1.5 }}>How encouraging should it sound?</SectionTitle>
          <Box role="radiogroup" aria-label="Tone" sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {TONE_OPTIONS.map((t) => {
              const selected = local.tone === t.value;
              return (
                <Chip
                  key={t.value}
                  label={t.label}
                  onClick={() => update({ tone: t.value })}
                  role="radio"
                  aria-checked={selected}
                  sx={{
                    fontWeight: 700,
                    height: 44,
                    px: 0.5,
                    backgroundColor: selected ? tutorTokens.accent : tutorTokens.surface,
                    color: selected ? '#fff' : tutorTokens.ink,
                    border: `1px solid ${selected ? tutorTokens.accent : tutorTokens.border}`,
                    ...focusRingSx,
                  }}
                />
              );
            })}
          </Box>

          <Divider sx={{ my: 2, borderColor: tutorTokens.border }} />

          <SectionTitle icon={FeedbackIcon} sx={{ mb: 0.5 }}>How should it respond to mistakes?</SectionTitle>
          <Typography variant="caption" sx={{ color: tutorTokens.inkSoft, display: 'block', mb: 1 }}>
            This only shapes the wording of feedback — the agent doesn't score or grade work.
          </Typography>
          <Box role="radiogroup" aria-label="Feedback style">
            {FEEDBACK_STYLES.map((f) => (
              <OptionCard
                key={f.value}
                selected={local.feedbackStyle === f.value}
                label={f.label}
                consequence={f.example}
                onSelect={() => update({ feedbackStyle: f.value })}
                hoverLift={false}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: tutorTokens.layout.sectionGap }}>
          <Box sx={cardSx}>
            <SectionTitle icon={WelcomeIcon} sx={{ mb: 0.5 }}>Welcome Message</SectionTitle>
            <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft, mb: 2 }}>The first thing a student sees when they open a session</Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              placeholder={`"Hi! I'm here to help you practice fraction word problems — want to start with today's homework, or pick your own problem?"`}
              value={local.welcomeMessage || ''}
              onChange={(e) => update({ welcomeMessage: e.target.value.slice(0, 240) })}
              inputProps={{ maxLength: 240, 'aria-label': 'Welcome message', 'aria-describedby': 'config-welcome-count' }}
              sx={{ mb: 0.5, '& .MuiOutlinedInput-root': { borderRadius: `${tutorTokens.radiusMd}px` } }}
            />
            <Typography id="config-welcome-count" variant="caption" sx={{ display: 'block', textAlign: 'right', color: tutorTokens.inkFaint }}>
              {(local.welcomeMessage || '').length}/240
            </Typography>
          </Box>

          <Box sx={cardSx}>
            <TutoringRulesField
              value={local.tutoringRules}
              onChange={(v) => update({ tutoringRules: v })}
              idPrefix="config-tutoring-rules"
            />
          </Box>

          <Box sx={cardSx}>
            <SectionTitle icon={ActivitiesIcon} sx={{ mb: 0.5 }}>Activities</SectionTitle>
            <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft, mb: 2 }}>What this agent can run with students</Typography>
            {[
              ['stepByStep', 'Step-by-step problem solving', 'Students work problems with the agent checking each line'],
              ['quizzes', 'Weekly quizzes', 'Auto-generated from topics covered'],
              ['flashcards', 'Flashcards', 'Formula and vocabulary decks'],
            ].map(([key, label, desc], i, arr) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 1.25,
                  borderBottom: i < arr.length - 1 ? `1px solid ${tutorTokens.divider}` : 'none',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...tutorTokens.type.body, fontWeight: 600, color: tutorTokens.ink }}>{label}</Typography>
                  <Typography sx={{ ...tutorTokens.type.label, color: tutorTokens.inkSoft }}>{desc}</Typography>
                </Box>
                <Switch
                  checked={!!local.activities[key]}
                  disabled={key === 'stepByStep'}
                  onChange={(e) => update({ activities: { ...local.activities, [key]: e.target.checked } })}
                  inputProps={{ 'aria-label': `${label} — ${key === 'stepByStep' ? 'always on' : local.activities[key] ? 'on' : 'off'}` }}
                />
              </Box>
            ))}
          </Box>

          <Box sx={cardSx}>
            <SectionTitle icon={ClassesIcon} sx={{ mb: 1.5 }}>Assigned Classes</SectionTitle>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {groups.map((g) => {
                const selected = local.assignedGroupIds.includes(g.id);
                return (
                  <Chip
                    key={g.id}
                    icon={selected ? <CheckIcon sx={{ fontSize: 16 }} /> : undefined}
                    label={g.school ? `${g.school} · ${g.name}` : g.name}
                    onClick={() => toggleGroup(g.id)}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: selected ? tutorTokens.accentLight : tutorTokens.surface,
                      color: selected ? tutorTokens.accent : tutorTokens.inkSoft,
                      border: `1px solid ${selected ? tutorTokens.accent : tutorTokens.border}`,
                      ...focusRingSx,
                    }}
                  />
                );
              })}
              <Tooltip title="Saves your other edits first, then opens the builder to assign classes">
                <span>
                  <Chip
                    label="+ Assign"
                    variant="outlined"
                    onClick={openBuilder}
                    disabled={saving}
                    sx={{
                      fontWeight: 600,
                      borderStyle: 'dashed',
                      color: tutorTokens.accent,
                      borderColor: tutorTokens.accent,
                      ...focusRingSx,
                    }}
                  />
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Misconceptions gets its own full-width row below the two-column
          grid, not a slot inside it — this is the one panel with no upper
          bound on length (a teacher can add as many as they want), and
          giving it a fixed column width was the whole source of the
          column-balance problem here (see the comment above `cardSx`). */}
      <Box sx={{ ...cardSx, mt: tutorTokens.layout.sectionGap }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
          <IconBadge icon={MisconceptionIcon} size={32} />
          <Typography component="h5" sx={{ ...tutorTokens.type.subtitle, color: tutorTokens.ink, m: 0 }}>
            Misconceptions
          </Typography>
        </Box>
        <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft, mb: 2 }}>
          Mistakes the agent watches for and how it responds
        </Typography>
        {/* Constrained to a real reading measure with the mistake as a
            title line and the response demoted under it — the old version
            ran one small-grey "→ response" line the full card width,
            unscannable and prone to wrapping mid-word at that measure. The
            `ch` cap lives on each Typography, not the wrapping Box: `ch`
            resolves against the *element's own* font-size, and the title
            (`body`, 14px) and response (`label`, 12.5px) are different
            sizes — capping the shared parent would measure at whichever
            size the parent inherits, not either child's actual size,
            silently widening the response line past its own real-character
            target. Measured on the response line at 12.5px: `68ch` first
            rendered as 550px, which is ~85-88 actual (proportional-font)
            characters per line — `ch` is the width of this font's "0"
            glyph, not the average glyph width real prose renders at, so it
            overshoots the intended character count. `48ch` is calibrated
            off that measurement to land in the low-to-mid 60s of real
            characters, comfortably inside the 45-75ch comfortable range.
            `wordBreak: break-word` is the graceful-overflow floor for a
            mistake phrase longer than the measure with no spaces to wrap
            on. */}
        {local.misconceptions.map((m) => (
          <Box
            key={m.id}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              p: 1.5,
              mb: 1,
              borderRadius: `${tutorTokens.radiusMd}px`,
              backgroundColor: tutorTokens.surface,
              border: `1px solid ${tutorTokens.border}`,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ ...tutorTokens.type.body, fontWeight: 700, color: tutorTokens.ink, maxWidth: '48ch', wordBreak: 'break-word' }}>
                {m.mistake}
              </Typography>
              {m.response && (
                <Typography sx={{ ...tutorTokens.type.label, fontWeight: 500, color: tutorTokens.inkSoft, mt: 0.35, maxWidth: '48ch', wordBreak: 'break-word' }}>
                  → {m.response}
                </Typography>
              )}
            </Box>
            {/* `ml: 'auto'` is load-bearing, not leftover — the content
                Box above is `flex: 1` but also capped at `maxWidth: 48ch`,
                so once the row is wider than that cap (the normal case in
                this full-width card), `flex: 1`'s un-used grow space has
                nowhere to go and would otherwise strand this button right
                after the capped text instead of at the row's trailing
                edge. */}
            <IconButton
              size="small"
              onClick={() => removeMisconception(m.id)}
              aria-label={`Remove misconception: ${m.mistake}`}
              sx={{ width: 44, height: 44, ml: 'auto', flexShrink: 0, color: tutorTokens.inkSoft, '&:hover': { color: tutorTokens.incorrect }, ...focusRingSx }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <TextField
          size="small"
          fullWidth
          placeholder="e.g. Drops the negative when dividing"
          value={misconceptionDraft.mistake}
          onChange={(e) => setMisconceptionDraft((d) => ({ ...d, mistake: e.target.value }))}
          inputProps={{ 'aria-label': 'Misconception — what students get wrong' }}
          sx={{ mb: 1, maxWidth: 640, '& .MuiOutlinedInput-root': { borderRadius: `${tutorTokens.radiusSm}px` } }}
        />
        <TextField
          size="small"
          fullWidth
          placeholder="How should the agent respond? (optional)"
          value={misconceptionDraft.response}
          onChange={(e) => setMisconceptionDraft((d) => ({ ...d, response: e.target.value }))}
          inputProps={{ 'aria-label': "Agent's response (optional)" }}
          sx={{ mb: 1, maxWidth: 640, '& .MuiOutlinedInput-root': { borderRadius: `${tutorTokens.radiusSm}px` } }}
        />
        <Button
          startIcon={<AddIcon />}
          onClick={addMisconception}
          sx={{ color: tutorTokens.accent, fontWeight: 700, minHeight: 44, ...focusRingSx }}
        >
          Add a misconception
        </Button>
      </Box>
      </Box>
    </Box>
  );
};

export default MathTutorConfig;
